import { ColumnDef } from '@tanstack/react-table';
import { format, formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';
import { EventNameDisplay } from 'src/components/Events/EventInfo';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import { BaseCell } from 'src/components/TableV2/cells';
import { CampaignSalesAggregate } from 'src/contexts/AdCampaignsDataContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { createInventoryEventUrl } from 'src/navigations/Routes/InventoryEvent/InventoryEvent.utils';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronUpIconV2 } from 'src/svgs/Viagogo/goicons/ChevronUpIconV2';
import * as styles from 'src/tables/SponsoredListings/SponsoredListingsTable.css';
import { TableShimmeringDiv } from 'src/tables/Table';
import { MOCK_SPONSORED_LISTINGS_DATA } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  applyNumberFormatting,
  formatPercent,
} from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
  TicketClassInfo,
  UiCurrency,
} from 'src/WebApiController';

export type CampaignsTableRow = {
  id: string;
  campaignId: number;
  name: string;
  eventId?: number;
  eventName?: string;
  eventNameParts?: string[];
  eventDate?: Date;
  currency?: UiCurrency;
  salesData?: CampaignSalesAggregate;
  hasChildren: boolean;
  campaign?: SponsoredListingsCampaign;
  flight?: SponsoredListingsFlight;
  flights?: SponsoredListingsFlight[];
};

export const getTableColumnDefinitions = (
  onStartWork: () => void,
  onStopWork: (success: boolean) => void,
  disabled?: boolean
): ColumnDef<CampaignsTableRow>[] => [
  {
    id: 'event-name',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Event} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventName,
    cell: function Cell({ getValue, row: { original } }) {
      const eventName = getValue<string | undefined>();

      if (!original) {
        return <TableShimmeringDiv />;
      }

      if (!original.eventNameParts) {
        return null;
      }

      return (
        <BaseCell align="left">
          <Button
            variant="text"
            title={eventName}
            onClick={(e) => {
              if (original.eventId) {
                window.open(
                  createInventoryEventUrl(original.eventId.toString(), true),
                  '_blank',
                  'noopener noreferrer'
                );
              }
            }}
          >
            <EventNameDisplay
              eventName={original.eventNameParts[0]}
              eventNameConnector={original.eventNameParts[1]}
              eventSubName={original.eventNameParts[2]}
            />
          </Button>
        </BaseCell>
      );
    },
  },
  {
    id: 'eventDate',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.EventDate} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.eventDate,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = getValue<Date | undefined>();
      if (!value) {
        return null;
      }

      const eventDate = format(value, 'MMM d - h:mm a');

      return (
        <BaseCell align="left" title={value.toString()}>
          {eventDate}
        </BaseCell>
      );
    },
  },
  {
    id: 'campaign',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.CampaignName} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.name,
    sortingFn: 'textCaseSensitive',
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      const value = getValue<string>();
      return (
        <BaseCell align="left">
          {original.campaign ? <b>{value}</b> : value}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              <Content id={ContentId.CurrentFlightsTotal} />
            </span>
            <span>
              <Content id={ContentId.CampaignsTotal} />
            </span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'startDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.StartDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      (data.campaign?.dateRange ?? data.flight?.dateRange)?.start,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }
      const value = getValue<string | undefined>();
      if (!value) {
        return null;
      }

      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'endDate',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.EndDateTime} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) =>
      (data.campaign?.dateRange ?? data.flight?.dateRange)?.end,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }
      const value = getValue<string | undefined>();
      if (!value) {
        return null;
      }

      const dateValue = value ? new Date(value).toISOString() : value;

      return (
        <BaseCell align="right">
          {formatInTimeZone(
            dateValue ? new Date(dateValue) : new Date(),
            'UTC',
            'M/d/yyyy h:mm aa'
          )}
        </BaseCell>
      );
    },
  },
  {
    id: 'revenue',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Revenue} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.salesData,
    enableSorting: false,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const salesData = getValue<CampaignSalesAggregate | undefined>();
      let revenue = salesData?.totalNetProceeds;

      if (original.flights && salesData) {
        // For aggregated flights, sum up revenue from all flights
        revenue = original.flights.reduce((sum, flight) => {
          const flightData = salesData.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
      } else if (original.flight && salesData) {
        // If this is a flight row, calculate revenue proportionally
        const flightData =
          salesData.flightsAggregate?.[original.flight.flightId];

        revenue = flightData?.totalNetProceeds;
      }

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $100 and $10,000
        revenue = Math.floor(Math.random() * 9900) + 100;
      }

      return (
        <BaseCell align="right" style={{ padding: vars.spacing.xs }}>
          {!!revenue && (
            <div
              className={revenue > 0 ? styles.revenuePositiveCell : undefined}
            >
              {applyNumberFormatting({
                inputNumber: revenue,
                currencyCode: original.currency?.code,
                currencyDecimalDigits: original.currency?.dec,
              })}
            </div>
          )}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              {!!original?.salesData?.flightsAggregate?.[0].totalNetProceeds &&
                applyNumberFormatting({
                  inputNumber:
                    original.salesData.flightsAggregate[0].totalNetProceeds,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
            <span>
              {!!original.salesData?.totalNetProceeds &&
                applyNumberFormatting({
                  inputNumber: original.salesData.totalNetProceeds,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'spend',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.AdSpend} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up spend from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      }
      return data.campaign?.totalSpend ?? data.flight?.flightSpend;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const spend = getValue<number | undefined>();

      return (
        <BaseCell align="right">
          {!!spend &&
            applyNumberFormatting({
              inputNumber: spend,
              currencyCode: original.currency?.code,
              currencyDecimalDigits: original.currency?.dec,
            })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              {!!original.flight?.flightSpend &&
                applyNumberFormatting({
                  inputNumber: original.flight.flightSpend,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
            <span>
              {!!original.campaign?.totalSpend &&
                applyNumberFormatting({
                  inputNumber: original.campaign.totalSpend,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'roas',
    header: () => <TableHeaderCellDiv align="right">ROAS</TableHeaderCellDiv>,
    accessorFn: (data) => data.salesData,
    enableSorting: false,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const salesData = getValue<CampaignSalesAggregate | undefined>();

      let revenue = salesData?.totalNetProceeds ?? 0;
      let spend = original.campaign?.totalSpend;

      if (original.flights && salesData) {
        // For aggregated flights, sum up revenue and spend from all flights
        revenue = original.flights.reduce((sum, flight) => {
          const flightData = salesData?.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.totalNetProceeds || 0);
        }, 0);
        spend = original.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      } else if (original.flight && salesData) {
        // If this is a flight row, calculate revenue proportionally
        const flightData =
          salesData.flightsAggregate?.[original.flight.flightId];

        revenue = flightData?.totalNetProceeds;
        spend = original.flight?.flightSpend;
      }

      // In development, assign random revenue if the actual value is 0
      if (MOCK_SPONSORED_LISTINGS_DATA && !revenue) {
        // Generate a random revenue between $1 and budget * 10
        revenue =
          Math.floor(
            Math.random() *
              ((original.campaign?.totalBudget ??
                original?.flight?.flightBudget ??
                1) *
                10 -
                1)
          ) + 1;
      }

      const roas = spend ? revenue / spend : 0;
      return (
        <BaseCell align="right">{!!roas && formatPercent(roas, 2)}</BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      const revenue =
        original?.salesData?.flightsAggregate?.[0].totalNetProceeds ?? 0;
      const spend = original?.flight?.flightSpend;

      const roas = spend ? revenue / spend : 0;

      const totalRevenue = original.salesData?.totalNetProceeds ?? 0;
      const totalSpend = original.campaign?.totalSpend;

      const totalRoas = totalSpend ? totalRevenue / totalSpend : 0;

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>{!!roas && formatPercent(roas, 2)}</span>
            <span>{!!totalRevenue && formatPercent(totalRoas, 2)}</span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'sales-count',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Sales} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.salesData,
    enableSorting: false,
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const salesData = getValue<CampaignSalesAggregate | undefined>();
      let saleCount = salesData?.saleCount;

      if (original.flights && salesData) {
        // For aggregated flights, sum up sale counts from all flights
        saleCount = original.flights.reduce((sum, flight) => {
          const flightData = salesData?.flightsAggregate?.[flight.flightId];
          return sum + (flightData?.saleCount || 0);
        }, 0);
      } else if (original.flight && salesData) {
        // If this is a flight row, get sale count for the specific flight
        const flightData =
          salesData.flightsAggregate?.[original.flight.flightId];

        saleCount = flightData?.saleCount;
      }

      return <BaseCell align="right">{saleCount}</BaseCell>;
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              {original?.salesData?.flightsAggregate?.[0].saleCount || ' '}
            </span>
            <span>{original.salesData?.saleCount || ' '}</span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'budget',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Budget} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      if (data.flights) {
        // For aggregated flights, sum up budget from all flights
        return data.flights.reduce(
          (sum, flight) => sum + (flight.flightBudget || 0),
          0
        );
      }
      return data.campaign?.totalBudget ?? data.flight?.flightBudget;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const budget = getValue() as number | null;

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: budget,
            currencyCode: original.currency?.code,
            currencyDecimalDigits: original.currency?.dec,
          })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              {!!original.flight?.flightBudget &&
                applyNumberFormatting({
                  inputNumber: original.flight.flightBudget,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
            <span>
              {!!original.campaign?.totalBudget &&
                applyNumberFormatting({
                  inputNumber: original.campaign.totalBudget,
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'budget-remaining',
    header: () => (
      <TableHeaderCellDiv align="right">
        <Content id={ContentId.Remaining} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => {
      let budget: number;
      let spend: number;
      if (data.flights) {
        // For aggregated flights, sum up budget and spend from all flights
        budget = data.flights.reduce(
          (sum, flight) => sum + (flight.flightBudget || 0),
          0
        );
        spend = data.flights.reduce(
          (sum, flight) => sum + (flight.flightSpend || 0),
          0
        );
      } else {
        // Calculate budget left for individual campaign or flight
        budget = data.campaign?.totalBudget ?? data.flight?.flightBudget ?? 0;
        spend = data.campaign?.totalSpend ?? data.flight?.flightSpend ?? 0;
      }

      const remaining = budget - spend;

      return remaining;
    },
    cell: function Cell({ getValue, row: { original } }) {
      if (!original) {
        return <TableShimmeringDiv />;
      }

      const remaining = getValue<number>();

      return (
        <BaseCell align="right">
          {applyNumberFormatting({
            inputNumber: remaining,
            currencyCode: original.currency?.code,
            currencyDecimalDigits: original.currency?.dec,
          })}
        </BaseCell>
      );
    },
    footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
      const original = table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      const value = accessorFn?.(original, -1) as number | null;

      if (value == null) {
        return null;
      }

      return (
        <BaseCell align="right" style={{ fontWeight: 'bolder' }}>
          <Stack direction="column">
            <span>
              {(!!original.flight?.flightBudget ||
                !!original.flight?.flightSpend) &&
                applyNumberFormatting({
                  inputNumber:
                    (original.flight?.flightBudget ?? 0) -
                    (original.flight?.flightSpend ?? 0),
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
            <span>
              {(!!original.campaign?.totalBudget ||
                !!original.campaign?.totalSpend) &&
                applyNumberFormatting({
                  inputNumber:
                    (original.campaign.totalBudget ?? 0) -
                    (original.campaign.totalSpend ?? 0),
                  currencyCode: original.currency?.code,
                  currencyDecimalDigits: original.currency?.dec,
                })}
            </span>
          </Stack>
        </BaseCell>
      );
    },
  },
  {
    id: 'target',
    header: () => (
      <TableHeaderCellDiv align="left">
        <Content id={ContentId.Target} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaign?.targetTicketClassIds,
    cell: function Cell({ getValue, row: { original } }) {
      const { venueMapInfo } = useEventMapContext();
      const allZoneTxt = useContent(ContentId.AllZones);

      // Extract all TicketClassInfo from venueMapInfo as a record keyed by ticketClassId
      const ticketClassesById = useMemo(() => {
        if (!venueMapInfo) return {};

        return venueMapInfo.sections
          .flatMap((s) => s.rows.map((r) => r.tktClass))
          .reduce(
            (record, ticketClass) => {
              if (ticketClass?.id) {
                record[ticketClass.id] = ticketClass;
              }
              return record;
            },
            {} as Record<number, TicketClassInfo>
          );
      }, [venueMapInfo]);

      if (!original) {
        return <TableShimmeringDiv />;
      }
      if (original.flights) {
        return null;
      }

      const tktClassIds = getValue<number[] | undefined>();

      // Display target information
      let target = allZoneTxt;
      if (tktClassIds?.length && Object.keys(ticketClassesById).length > 0) {
        const targetedClasses = tktClassIds
          .map((id) => ticketClassesById[id]?.name)
          .filter(Boolean);

        if (targetedClasses.length > 0) {
          target = targetedClasses.join(', ');
        }
      }

      return <BaseCell align="left">{target}</BaseCell>;
    },
  },
  {
    id: 'actions',
    header: () => (
      <TableHeaderCellDiv align="center">
        <Content id={ContentId.Actions} />
      </TableHeaderCellDiv>
    ),
    accessorFn: (data) => data.campaign,
    cell: function Cell({
      getValue,
      row: { getCanExpand, getIsExpanded, toggleExpanded, original },
    }) {
      const campaign = getValue<SponsoredListingsCampaign | undefined>();

      const canExpand = getCanExpand();
      if (!canExpand) {
        return null;
      }
      const isExpanded = getIsExpanded();
      const Icon = isExpanded ? ChevronUpIconV2 : ChevronDownIconV2;

      if (!original) {
        return <TableShimmeringDiv />;
      }

      return (
        <BaseCell
          align="center"
          style={{ padding: vars.spacing.sm, paddingRight: vars.spacing.m }}
        >
          <Stack gap="m" alignItems="center" justifyContent="center">
            {campaign && (
              <>
                <UpdateCampaignStateButton
                  iconOnly
                  campaign={campaign}
                  state={
                    campaign.campaignState === AdCampaignState.Active
                      ? AdCampaignState.Paused
                      : AdCampaignState.Active
                  }
                  onBeforePause={onStartWork}
                  onAfterPause={onStopWork}
                  disabled={disabled}
                />
                <BoostListingButton
                  campaign={campaign}
                  iconOnly
                  disabled={disabled}
                />
              </>
            )}
            <Icon
              size={vars.iconSize.s}
              withHoverEffect
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleExpanded(!isExpanded);
              }}
            />
          </Stack>
        </BaseCell>
      );
    },
  },
];
